import * as stylex from '@stylexjs/stylex'
import { useMemo, useState, type ReactNode } from 'react'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'
import { type ObjectFit } from '../utils/makeExtendStyle/extendStyles'
import { ImageBase, type ImageBaseProps } from './ImageBase'

const dynamicStyles = stylex.create({
  width: (width: string | number) => ({ width }),
  height: (height: string | number) => ({ height }),
  objectFit: (objectFit: ObjectFit) => ({ objectFit }),
})

const _baseStyles = stylex.create({
  root: {
    display: 'flex',
    position: 'relative',
  },
  loadingFallback: {
    position: 'absolute',
    height: '100%',
    left: 0,
    top: 0,
    width: '100%',
  },
})

export interface ImageProps extends Omit<ImageBaseProps, 'xStyles'> {
  /** 圖片載入失敗時的替代內容（可為 ReactNode 或圖片 URL 字串） */
  fallback?: ReactNode
  /**
   * 圖片載入中時的替代內容。
   * 如果有定義 loadingFallback 則必須定義 width，否則會導致容器 loading 時無法取得正確的寬度。
   */
  loadingFallback?: ReactNode
  /** 覆寫內部子樣式 */
  xStyles?: {
    /** 外層容器樣式覆寫 */
    base?: Partial<XStyles<keyof typeof _baseStyles>>
    /** 內部 img 元素樣式覆寫 */
    imgBase?: ImageBaseProps['xStyles']
  }
}

/**
 * Image 封裝了圖片載入、錯誤處理與 fallback 邏輯。
 * 支援載入中佔位與載入失敗替代內容。
 */
export const Image = (props: ImageProps) => {
  const { fallback: fallbackProp, loadingFallback: loadingFallbackProp, src, onLoad, onError, objectFit, width, height, xStyles, rootStyle, ...rest } = props
  const [hasError, setHasError] = useState(false)
  const [isLoading, setIsLoading] = useState(!!src)
  const baseStyles = useXStyles(_baseStyles, xStyles?.base)

  const handleLoad: ImageBaseProps['onLoad'] = (event) => {
    onLoad?.(event)
    setHasError(false)
    setIsLoading(false)
  }

  const handleError: ImageBaseProps['onError'] = (event) => {
    onError?.(event)
    setHasError(true)
    setIsLoading(false)
  }

  const fallback = useMemo(() => {
    if (typeof fallbackProp === 'string') {
      return <ImageBase src={fallbackProp} {...rest} />
    }

    return fallbackProp
  }, [fallbackProp, rest])

  const loadingFallback = useMemo(() => {
    if (typeof loadingFallbackProp === 'string') {
      return <ImageBase src={loadingFallbackProp} {...rest} />
    }

    return loadingFallbackProp
  }, [loadingFallbackProp, rest])

  const shouldFallback = (!src || hasError)

  return (
    <div {...stylex.props(baseStyles.root, !!width && dynamicStyles.width(width), !!height && dynamicStyles.height(height), rootStyle)}>
      {shouldFallback && fallback}
      {isLoading && <div {...stylex.props(baseStyles.loadingFallback)}>{loadingFallback}</div>}
      {!shouldFallback && (
        <ImageBase
          src={src}
          onLoad={handleLoad}
          onError={handleError}
          width={width}
          height={height}
          xStyles={xStyles?.imgBase}
          objectFit={objectFit}
          {...rest}
        />
      )}
    </div>
  )
}
